import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "How React Query gives you almost everything you thought you needed GraphQL for",
  "description": "GraphQL solves every problem you've ever had talking to APIs on the frontend. Mention a switch to GraphQL to your team and they think it's a joke. Rewrite our entire app? oh you jokester. React Query is the perfect middle ground ❤️",
  "published": "2020-09-07T00:00:00.000Z",
  "hero": "./img/screenshot-1599495519940.png"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`Friend, how often do you write a piece of logic like this?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function Component() {
  const [data, setData] = useState(null)

  async function fetchData() {
    const res = await fetch("https://example.com/api")
    const json = await res.json()
    setData(json)
  }

  useEffect(() => {
    fetchData()
  }, [])

  if (!data) {
    return <p>Loading ...</p>
  } else {
    return <DoStuffWithData data={data} />
  }
}
`}</code></pre>
    <p>{`You've got state for your API result, an async fetching function, an effect that runs on component mount and calls `}<inlineCode parentName="p">{`fetchData`}</inlineCode>{`. Rendering shows either a loading state or your API result.`}</p>
    <p>{`Works great, solves the problem, done.`}</p>
    <h2 {...{
      "id": "fiddly-and-wasteful"
    }}>{`Fiddly and wasteful`}</h2>
    <p>{`You can write this in your sleep after 3 months on the job. Super common piece of boilerplate.`}</p>
    <p>{`And it's easy to make it smart! You can re-fetch data when a prop changes.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function Component({ itemId }) {
  const [data, setData] = useState(null)

  async function fetchData(itemId) {
    const res = await fetch(\`https://example.com/api/\${itemId}\`)
    const json = await res.json()
    setData(json)
  }

  useEffect(() => {
    setData(null)
    fetchData(itemId)
  }, [itemId])

  if (!data) {
    return <p>Loading ...</p>
  } else {
    return <DoStuffWithData data={data} />
  }
}
`}</code></pre>
    <p>{`Your effect re-runs when `}<inlineCode parentName="p">{`itemId`}</inlineCode>{` changes. Data re-fetches, component re-renders, UI looks fresh. 🧙‍♂️`}</p>
    <p>{`Pretty neat huh?`}</p>
    <p>{`Except boilerplate sucks my friend. Nobody likes writing boilerplate.`}</p>
    <p>{`And this stupid thing is always juuuuust custom enough that you can't extract into a custom hook. Every component and every API request are different. Not a lot. Just enough.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media3.giphy.com/media/fXK6nNrqoW9NNWWcPD/giphy-loop.mp4?cid=7cf28b0f7m88vjcoji2d8f1vx9aapqm9ex80m9r359rvd6fj&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <p>{`Oh and what happens when you render this component twice?`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<Component />
<Component />
`}</code></pre>
    <p>{`You spam users and servers with API request. Fetching the same data twice. Wasteful.`}</p>
    <p>{`Looks contrived in this example, happens a lot in big projects. A component that fetches its own data, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/wormhole-state-management/"
      }}>{`like it should`}</a>{`, sneaks into 3 different parts of your UI.`}</p>
    <p>{`Boom – 3 parallel requests for the same result. 🤦‍♀️`}</p>
    <h3 {...{
      "id": "time-for-global-state"
    }}>{`Time for global state?`}</h3>
    <p>{`The common reaction at this point is to add global state management.`}</p>
    <p>{`Add a library like Redux or MobX, maybe XState, hoist data fetching into a new layer of your app, build machinery to manage state in a central place and ...`}</p>
    <p>{`It's a lot of work. And a mistake.`}</p>
    <p>{`Codebase gets 3 orders of magnitude more complicated. Anything you do from now on has to happen in 3 places, touch a bunch of code, and everything depends on everything else. Your system needs constant tuning. Nothing is isolated.`}</p>
    <p>{`Bleh.`}</p>
    <h2 {...{
      "id": "you-can-try-graphql"
    }}>{`You can try GraphQL`}</h2>
    <p>{`As I've mentioned before, `}<a parentName="p" {...{
        "href": "https://swizec.com/blog/how-graphql-blows-rest-out-of-the-water/"
      }}>{`GraphQL blows REST out of the water`}</a>{`. Solves every problem I've ever had.`}</p>
    <p>{`Large unspecific payloads? Solved.`}</p>
    <p>{`Merging many requests into 1? Solved.`}</p>
    <p>{`Deduping API requests? Solved.`}</p>
    <p>{`Reusing API requests across components? Solved.`}</p>
    <p>{`Caching results and re-fetching when stale? Solved.`}</p>
    <p>{`You need `}<a parentName="p" {...{
        "href": "https://github.com/apollographql/apollo-client"
      }}>{`Apollo Client`}</a>{` for the last 3 benefits and hey that's what almost everyone uses. 🤘`}</p>
    <p>{`Our example looks like this with GraphQL:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`const QUERY = gql\`
    query GetData($itemId: String!) {
        data(itemId: $itemId) {
            ...
        }
    }
\`

function Component({ itemId }) {
  const { loading, data } = useQuery(QUERY, {
    variables: { itemId },
  })

  if (loading) {
    return <p>Loading ...</p>
  } else {
    return <DoStuffWithData data={data} />
  }
}
`}</code></pre>
    <p>{`You write a query for your data, pass it into the `}<inlineCode parentName="p">{`useQuery`}</inlineCode>{` hook, and Apollo handles the rest. Data is cached, re-fetched when needed, requests are deduped, and your component is simpler.`}</p>
    <p>{`Rendering twice`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`<Component />
<Component />
`}</code></pre>
    <p>{`makes a single API request.`}</p>
    <p>{`You can even make a custom hook and reuse in many places.`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`function graphqlMyData(itemId) {
  return useQuery(QUERY, {
    variables: { itemId },
  })
}
`}</code></pre>
    <p>{`Use that anywhere in your code and if you use the same `}<inlineCode parentName="p">{`itemId`}</inlineCode>{`, your requests are deduped. Use different ids and `}<em parentName="p">{`I think`}</em>{` Apollo is smart enough to merge your requests.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media1.giphy.com/media/l4FGvUYI0tETAQwGk/giphy-loop.mp4?cid=7cf28b0fly8exp7edzacp7i51b4wjnwcik3a550357kwk5xi&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h2 {...{
      "id": "good-luck-using-graphql-with-an-existing-backend"
    }}>{`Good luck using GraphQL with an existing backend`}</h2>
    <p>{`Your existing backend uses REST doesn't it my friend?`}</p>
    <p>{`Yeah, me too. Suggest we could solve `}<inlineCode parentName="p">{`CurrentProblem`}</inlineCode>{` with GraphQL and people brush me off as a joke.`}</p>
    <p>{`Oh Swiz you so funny, of course we can't rewrite our entire ecosystem to use GraphQL. Cute, keep joking you jokester 😂`}</p>
    <p>{`You've got a system that works. A system that's more than just an API reading the database and spitting out JSON.`}</p>
    <p>{`Bet you there's heaps of business logic in there. Data validation, wrangling, reshaping, confirming everything works, keeping different systems talking to each other, and ensuring `}<em parentName="p">{`you`}</em>{` have less work to do on the frontend.`}</p>
    <p>{`That's why systems like Hasura are a bad idea if you ask me. `}<em parentName="p">{`You don't want to expose your entire database model to the frontend raw.`}</em>{` Ever.`}</p>
    <p><strong parentName="p">{`You need a well-designed API that hides underlying complexity.`}</strong></p>
    <p>{`That makes GraphQL fantastic for new projects and terrible for existing systems. It's too much work to switch.`}</p>
    <h2 {...{
      "id": "react-query-to-the-rescue"
    }}>{`React Query to the rescue`}</h2>
    <p><a parentName="p" {...{
        "href": "https://react-query.tanstack.com/"
      }}>{`React Query`}</a>{` solves almost all those problems `}<em parentName="p">{`without rewriting your backend.`}</em></p>
    <p>{`Keep using REST, get almost all the benefits of GraphQL.`}</p>
    <p><img parentName="p" {...{
        "src": "gigphy:excited",
        "alt": null
      }}></img></p>
    <p>{`Here's how it works:`}</p>
    <ol>
      <li parentName="ol">{`Write your data fetching functions`}</li>
      <li parentName="ol">{`Wrap them in a named query`}</li>
      <li parentName="ol">{`React Query handles caching, deduping, re-fetching, and loading states`}</li>
    </ol>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function fetchData(itemId) {
  const res = await fetch(\`https://example.com/api/\${itemId}\`)
  const json = await res.json()
  return json
}

function Component({ itemId }) {
  const { isLoading, data } = useQuery(["fetchData", itemId], fetchData)

  if (isLoading) {
    return <p>Loading ...</p>
  } else {
    return <DoStuffWithData data={data} />
  }
}
`}</code></pre>
    <p>{`Back to the example we had before.`}</p>
    <p><inlineCode parentName="p">{`fetchData`}</inlineCode>{` is a data fetching function, gets `}<inlineCode parentName="p">{`itemId`}</inlineCode>{` as an argument, and `}<em parentName="p">{`returns`}</em>{` JSON. This is important: React Query expects async functions that return JSON.`}</p>
    <p>{`You could use anything to generate that JSON. REST, GraphQL, Websocket, complicated javascript computation. Anything. React Query doesn't care ✌️`}</p>
    <p>{`Pass that into `}<inlineCode parentName="p">{`useQuery`}</inlineCode>{` with a key. You can use string keys, but I like the array version by default.`}</p>
    <p>{`An array key works like the dependency array of `}<inlineCode parentName="p">{`useEffect`}</inlineCode>{`. First argument is always a string, this names your query, the rest are dynamic props.`}</p>
    <p>{`React Query passes those props into your fetcher function as arguments and ensures your query re-runs when they change.`}</p>
    <p>{`I like to go even further and wrap queries into custom hooks:`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`async function fetchData(itemId) {
  const res = await fetch(\`https://example.com/api/\${itemId}\`)
  const json = await res.json()
  return json
}

function useItemData(itemId) {
  return useQuery(["fetchData", itemId], fetchData)
}
`}</code></pre>
    <p>{`Now you can put `}<inlineCode parentName="p">{`useItemData(itemId)`}</inlineCode>{` `}<em parentName="p">{`anywhere`}</em>{` in your codebase and React Query handles the rest.`}</p>
    <p>{`No worrying about global state, no dealing with caching, no fuss around loading states, no thinking about re-fetching, no worries around stale data, `}<em parentName="p">{`no rewriting your backend`}</em>{`.`}</p>
    <p><video parentName="p" {...{
        "style": {
          "margin": "auto auto",
          "display": "block",
          "maxWidth": "80%"
        },
        "autoPlay": true,
        "loop": true,
        "muted": true,
        "playsInline": true,
        "loading": "lazy"
      }}>{`
            `}<source parentName="video" {...{
          "src": "https://media2.giphy.com/media/l4pT6owvWSmHzKUtW/giphy-loop.mp4?cid=7cf28b0fv1o2yxfdvsda4u7cwy1hs7gfhfsnyu35o8lco2u0&rid=giphy-loop.mp4&ct=g",
          "type": "video/mp4"
        }}></source>{`
        `}</video></p>
    <h3 {...{
      "id": "what-react-query-cant-do"
    }}>{`What React Query can't do`}</h3>
    <p>{`React Query doesn't understand your queries like GraphQL libraries do. Means it can't merge multiple requests into 1 for you, can't reduce the size of your payloads, and can't help you validate the server returned what you were expecting.`}</p>
    <p>{`And for most usecases that's perfectly fine.`}</p>
    <p>{`Cheers,`}<br />{`
~Swizec`}</p>
    <p>{`PS: you can start using React Query alongside your existing code ✌️`}</p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      